import React, { useContext, useState, useEffect } from 'react';
import { Theme } from '@mui/material/styles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { Fragment } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { MsalContext } from '../Contexts/MsalContext';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { APIContext } from '../Contexts/APIContext';
import { formatDate } from '../Utilities/UtilityMethods';

const styles = {
    root: {
        color: 'black',
    },
    outterDiv: {
        minWidth: '350px',
        backgroundColor: '#fff',
        height: '100%',
        padding: '8px',
    },
    innerElements: {
        padding: '2px',
        width: '300px',
        marginLeft: '25px'
    },
    '@media (max-width: 485px)': {
        outterDiv: {
            padding: '8px',
            minWidth: '200px',
        },
        innerElements: {
            padding: '2px',
            width: '200px',
            marginLeft: '25px'
        },
    },
    checkIconSpan: {
        height: '36px',
        minimumHeight: '36px',
        maxHeight: '36px',
    },
    checkIcon: {
        marginBottom: '6px',
        marginLeft: '12px',
    },
    dividerFullWidth: {
        margin: (theme: Theme) => `5px 0 0 ${theme.spacing(2)}px`,
    },
    dividerInset: {
        margin: (theme: Theme) => `5px 0 0 ${theme.spacing(9)}px`,
        float: 'right',
    },
    listItem: {
        paddingTop: '0px',
        paddingBottom: '0px',
    },
    marginTenPx: {
        marginTop: '10px',
        marginBottom: '10px',
    },
    ownItem: {
        backgroundColor: '#ccffdd',
    },
    userItem: {
        backgroundColor: '#f2f2f2',
    },
    noBreak: {
        whiteSpace: 'nowrap',
    }
}

interface INotesProps {
    systemPK: string;
    monitoring?: boolean;
}

class NoteModel {
    public author: string;
    public timestamp: Date;
    public systemPk: string;
    public notes: string;
    public type: string;
}

class NoteType {
    public type: string;
    public tooltip: string;
}

export default function Notes({ systemPK, monitoring }: INotesProps) {
    const { msalFetch } = useContext(MsalContext);
    const { post } = useContext(APIContext);
    const [notes, setNotes] = useState<NoteModel[]>([]);
    const [name, setName] = useState('');
    const [newNote, setNewNote] = useState('');
    const [noteTypes, setNoteTypes] = useState<NoteType[]>([]);
    const [noteType, setNoteType] = useState<string>('Normal');

    useEffect(() => {
        msalFetch('api/User/getUserName')
            .then(response => response.text())
            .then(data => {
                setName(data);
            });

        fetchData();
    }, []);

    const fetchData = () => {
        msalFetch('api/Info/GetCallNotes?systemPk=' + encodeURIComponent(systemPK))
            .then(response => response.json())
            .then(Data => {
                setNotes(Data);
                setNewNote('');
            });

        msalFetch('api/Info/GetNoteTypes')
            .then(response => response.json())
            .then(Data => {
                setNoteTypes(Data);
            });
    }

    const onChange = (e) => setNewNote(e.target.value);

    const addNote = (e) => {
        post('Info', 'AddCallNote', ({
            systemPK: systemPK,
            note: newNote,
            type: noteType
        }),
            () => fetchData())
    }

    const handleNoteTypeChange = (event: any) => {
        setNoteType(event.target.value as string);
    }

    return (
        <Grid container sx={styles.outterDiv} direction="column">
            {!monitoring &&
                <Typography variant="subtitle1" gutterBottom>Callfinder Notes</Typography>
            }
            <Grid>
                <List>
                    {
                        notes.map((note) => {

                            let userClass = styles.userItem;

                            if (name === note.author) {
                                userClass = styles.ownItem;
                            }

                            return (
                                note.type !== 'Client Summary' &&
                                <Fragment key={note.notes}>
                                    <Grid container sx={userClass} justifyContent="space-between">
                                        <Grid>
                                            <Typography color="textSecondary" variant="caption">{note.author} {note.type && ` - ${note.type}`}</Typography>
                                        </Grid>
                                        <Grid>
                                            <Typography variant="caption">{formatDate(note.timestamp)}</Typography>
                                        </Grid>
                                    </Grid>
                                    <ListItem sx={styles.listItem}>
                                        <Typography> {note.notes} </Typography>
                                    </ListItem>
                                    <Divider component="section" />
                                </Fragment>
                            )
                        })
                    }
                </List>
            </Grid>
            <Grid >
                <Grid container spacing={1} alignItems="flex-end">
                    <Grid size={9}>
                        <TextField multiline fullWidth value={newNote} onChange={onChange} />
                    </Grid>
                    <Grid size={3} >
                        <Tooltip title={noteTypes.find(f => f.type == noteType)?.tooltip} placement="right">
                            <Select
                                fullWidth
                                value={noteType}
                                onChange={handleNoteTypeChange}
                                sx={{ height: '27px', marginBottom: '2px' }}
                            >
                                {
                                    noteTypes.map((noteType) => {
                                        return (
                                            <MenuItem
                                                key={noteType.type}
                                                value={noteType.type}>
                                                {noteType.type}
                                            </MenuItem>
                                        );
                                    })
                                }
                            </Select>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            onClick={addNote}
                            color="secondary"
                            fullWidth
                            sx={{ height: '27px' }}>
                            Add Note
                        </Button>
                    </Grid>
                </Grid>
            </Grid >
        </Grid >
    );
}